import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Card from '../Card';
import Button from '../Button';
import TextButton from '../TextButton';
import Text from '../Text';
import Heading from '../Heading';
import Proportion from '../Proportion';
import Tooltip from '../Tooltip';
import MediaOverlay from '../MediaOverlay';
import { DataHook } from './CardGalleryItem.constants';
import { classes, st } from './CardGalleryItem.st.css.js';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import { DragHandle } from '../DragHandle/DragHandle';
import Image from '../Image';
import { DraggableContainer } from '../DraggableContainer/DraggableContainer';
class CardGalleryItem extends PureComponent {
    constructor() {
        super(...arguments);
        this.state = {
            isHovered: false,
            isFocused: false,
        };
        this._onMouseEnter = () => {
            this.setState({ isHovered: true });
        };
        this._onMouseLeave = () => {
            this.setState({ isHovered: false });
        };
        this._onFocus = e => {
            try {
                if (process.env.NODE_ENV === 'test' ||
                    e.target.matches(':focus-visible')) {
                    this.setState({ isFocused: true });
                }
            }
            catch {
                this.setState({ isFocused: true });
            }
        };
        this._onBlur = () => {
            this.setState({ isFocused: false });
        };
    }
    _hasFooter() {
        const { title, subtitle, suffix, imagePlacement } = this.props;
        return !!(title || subtitle || suffix || imagePlacement === 'side');
    }
    _renderBadge() {
        return (React.createElement("div", { className: classes.badgeWrapper, "data-hook": DataHook.Badge }, this.props.badge));
    }
    _renderDragHandle() {
        const { dragHandleProps, dragging, dragDisabled } = this.props;
        return (React.createElement(DragHandle, { ...dragHandleProps, dragging: dragging, disabled: dragDisabled, dataHook: DataHook.dragHandle }));
    }
    _renderFooter() {
        const { title, subtitle, size, suffix, draggable, imagePlacement, backgroundImageNode, backgroundImageUrl, settingsMenu, } = this.props;
        const heading = size === 'medium' && imagePlacement !== 'side' ? (React.createElement(Heading, { size: "small", ellipsis: true, dataHook: DataHook.Title }, title)) : (React.createElement(Text, { size: "small", weight: "normal", ellipsis: true, dataHook: DataHook.Title }, title));
        const textSize = imagePlacement === 'side' ? 'tiny' : size === 'medium' ? 'small' : 'tiny';
        return (React.createElement(React.Fragment, null,
            imagePlacement === 'top' && React.createElement(Card.Divider, null),
            React.createElement("div", { className: classes.footer, "data-hook": DataHook.footer },
                draggable && (React.createElement("div", { className: classes.dragHandleWrapper }, this._renderDragHandle())),
                imagePlacement === 'side' && (React.createElement("div", { className: st(classes.footerImage, { size }), "data-hook": DataHook.footerImage }, backgroundImageUrl ? (React.createElement(Image, { src: backgroundImageUrl })) : (backgroundImageNode))),
                React.createElement("div", { className: classes.footerContent },
                    heading,
                    React.createElement(Text, { size: textSize, secondary: true, ellipsis: true, dataHook: DataHook.Subtitle }, subtitle)),
                suffix && (React.createElement("div", { className: classes.footerSuffix, "data-hook": DataHook.suffix }, suffix)),
                imagePlacement === 'side' &&
                    settingsMenu &&
                    this._renderSettingsMenu())));
    }
    _renderActions() {
        const { primaryActionProps: { label, disabled, disabledMessage }, secondaryActionProps, draggable, } = this.props;
        const primaryAction = (React.createElement(WixDesignSystemContext.Consumer, null, ({ newColorsBranding }) => (React.createElement(Button, { dataHook: DataHook.PrimaryAction, disabled: disabled, skin: newColorsBranding ? 'light' : 'standard', onPointerDown: draggable ? e => e.stopPropagation() : undefined }, label))));
        return (React.createElement("div", { className: classes.primaryAction, "data-hook": DataHook.HoverContent },
            disabled && disabledMessage ? (React.createElement(Tooltip, { disabled: !disabled, content: disabledMessage }, primaryAction)) : (primaryAction),
            secondaryActionProps && (React.createElement("div", { className: classes.secondaryAction },
                React.createElement(TextButton, { skin: "light", onClick: (event) => {
                        if (secondaryActionProps.onClick) {
                            secondaryActionProps.onClick(event);
                        }
                        event.stopPropagation();
                    }, dataHook: DataHook.SecondaryAction, onPointerDown: draggable
                        ? (e) => e.stopPropagation()
                        : undefined }, secondaryActionProps.label)))));
    }
    _renderSettingsMenu() {
        return (React.createElement("div", { className: classes.settingsMenu, "data-hook": DataHook.SettingsMenu }, this.props.settingsMenu));
    }
    render() {
        const { primaryActionProps, secondaryActionProps, dataHook, badge, backgroundImageUrl, backgroundImageNode, settingsMenu, size, draggable, dragging, droppable, dragDisabled, tabIndex, aspectRatio, imagePlacement, showSuffixOnHover, suffix, skin, skinVisibility, } = this.props;
        const { isHovered } = this.state;
        const withFooter = !!this._hasFooter();
        const showActions = !dragging && !droppable && !!(primaryActionProps || secondaryActionProps);
        const withFloatingDragHandle = !!draggable && !withFooter;
        const withHover = showActions || withFloatingDragHandle;
        const showShadow = skin === 'standard' && (skinVisibility === 'always' || isHovered);
        return (React.createElement(DraggableContainer, { draggable: draggable && !showActions, dragging: dragging, dim: true, dragDisabled: dragDisabled, tabIndex: tabIndex, className: st(classes.root, {
                withFooter,
                size,
                dragging,
                draggable,
                showActions,
                imagePlacement,
                showSuffixOnHover,
                suffix: !!suffix,
            }), "data-hook": dataHook, onFocus: this._onFocus, onBlur: this._onBlur },
            React.createElement("div", { className: classes.container, onMouseEnter: this._onMouseEnter, onMouseLeave: this._onMouseLeave, onClick: primaryActionProps.onClick, "data-hook": DataHook.Container },
                React.createElement(Card, { stretchVertically: true, showShadow: showShadow, className: st(classes.cardContainer, { skin, skinVisibility }) },
                    imagePlacement === 'top' && (React.createElement(Proportion, { aspectRatio: aspectRatio },
                        React.createElement(MediaOverlay, { media: backgroundImageUrl || backgroundImageNode || '', className: classes.overlay, hoverSkin: withHover ? 'dark' : undefined, hovered: this.state.isHovered || this.state.isFocused, dataHook: DataHook.HoverComponent },
                            showActions && (React.createElement(MediaOverlay.Content, { visible: "hover" }, this._renderActions())),
                            settingsMenu && showActions && (React.createElement(MediaOverlay.Content, { visible: "hover", placement: "top-end" }, this._renderSettingsMenu()))),
                        badge && this._renderBadge(),
                        React.createElement("div", { className: classes.dragHandleFloatingWrapper }, withFloatingDragHandle && this._renderDragHandle()))),
                    withFooter && this._renderFooter()))));
    }
}
CardGalleryItem.displayName = 'CardGalleryItem';
CardGalleryItem.propTypes = {
    badge: PropTypes.node,
    size: PropTypes.oneOf(['small', 'medium']),
    title: PropTypes.node,
    subtitle: PropTypes.node,
    suffix: PropTypes.node,
    draggable: PropTypes.bool,
    droppable: PropTypes.bool,
    dragHandleProps: PropTypes.object,
    dragging: PropTypes.bool,
    dragDisabled: PropTypes.bool,
    tabIndex: PropTypes.number,
    backgroundImageUrl: PropTypes.string,
    backgroundImageNode: PropTypes.node,
    primaryActionProps: PropTypes.shape({
        label: PropTypes.node,
        onClick: PropTypes.func,
        disabled: PropTypes.bool,
        disabledMessage: PropTypes.string,
    }).isRequired,
    secondaryActionProps: PropTypes.shape({
        label: PropTypes.node,
        onClick: PropTypes.func,
    }),
    settingsMenu: PropTypes.node,
    dataHook: PropTypes.string,
    aspectRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    imagePlacement: PropTypes.oneOf(['top', 'side']),
    showSuffixOnHover: PropTypes.bool,
    skin: PropTypes.oneOf(['standard', 'outlined']),
    skinVisibility: PropTypes.oneOf(['always', 'hover']),
};
CardGalleryItem.defaultProps = {
    aspectRatio: 1.6,
    primaryActionProps: {
        onClick: () => { },
    },
    size: 'medium',
    imagePlacement: 'top',
    showSuffixOnHover: false,
    skin: 'standard',
    skinVisibility: 'always',
};
export default CardGalleryItem;
//# sourceMappingURL=CardGalleryItem.js.map